<template>
  <div class="question-answer-node" :class="[{ 'is-child': isChild, 'is-deep': !usesInlineChildren, 'is-later-deep': nodeDepth > 2 }, displayMode]">
    <Question
      class="question-card"
      ref="questionControl"
      v-model="question.answer"
      :type="question.type"
      :title="question.title"
      :surveyType="question.surveyType"
      :isRequired="question.isRequired"
      :optionList="question.optionList"
      :uploaderNeedFileDescription="question.uploaderNeedFileDescription"
      :uploaderFileDescription="question.uploaderFileDescription"
      :uploaderFileTypeList="question.uploaderFileTypeList"
      :uploaderFileSize="question.uploaderFileSize"
      :uploaderTipList="question.uploaderTipList"
      :policyLink="question.policyLink"
      :errorMessage="errorMessageFormatter(question.errorMessage)"
      :autosize="autosize"
      :displayMode="displayMode"
      :questionNumber="questionNumber"
      :isNested="isChild"
      @change="$emit('answer-change', question, $event)"
      @update:uploaderFileDescription="question.uploaderFileDescription = $event"
      @file-upload="$emit('file-upload', question, ...arguments)"
    >
      <template #option-child="{ option }">
        <QuestionAnswerNode
          v-for="child in renderChildren && usesInlineChildren ? childrenForOption(option.value) : []"
          :key="child.id"
          :question="child"
          :questions="questions"
          :question-map="questionMap"
          :placement-map="placementMap"
          :display-mode="displayMode"
          :autosize="autosize"
          :error-message-formatter="errorMessageFormatter"
          :ancestor-ids="nextAncestorIds"
          @answer-change="$emit('answer-change', ...arguments)"
          @file-upload="$emit('file-upload', ...arguments)"
          @register-question="$emit('register-question', ...arguments)"
          @unregister-question="$emit('unregister-question', ...arguments)"
        />
      </template>
      <template #answer-child>
        <QuestionAnswerNode
          v-for="child in renderChildren && usesInlineChildren ? selectedChildren : []"
          :key="child.id"
          :question="child"
          :questions="questions"
          :question-map="questionMap"
          :placement-map="placementMap"
          :display-mode="displayMode"
          :autosize="autosize"
          :error-message-formatter="errorMessageFormatter"
          :ancestor-ids="nextAncestorIds"
          @answer-change="$emit('answer-change', ...arguments)"
          @file-upload="$emit('file-upload', ...arguments)"
          @register-question="$emit('register-question', ...arguments)"
          @unregister-question="$emit('unregister-question', ...arguments)"
        />
      </template>
    </Question>
    <QuestionAnswerNode
      v-for="child in renderChildren && !usesInlineChildren ? selectedChildren : []"
      :key="child.id"
      :question="child"
      :questions="questions"
      :question-map="questionMap"
      :placement-map="placementMap"
      :display-mode="displayMode"
      :autosize="autosize"
      :error-message-formatter="errorMessageFormatter"
      :ancestor-ids="nextAncestorIds"
      @answer-change="$emit('answer-change', ...arguments)"
      @file-upload="$emit('file-upload', ...arguments)"
      @register-question="$emit('register-question', ...arguments)"
      @unregister-question="$emit('unregister-question', ...arguments)"
    />
  </div>
</template>

<script>
import Question, { QUESTION_TYPE } from './question.vue'

export default {
  name: 'QuestionAnswerNode',
  components: { Question },
  props: {
    question: {
      type: Object,
      required: true
    },
    questionNumber: {
      type: Number,
      default: null
    },
    questionMap: {
      type: Object,
      required: true
    },
    questions: {
      type: Array,
      required: true
    },
    placementMap: {
      type: Object,
      required: true
    },
    displayMode: {
      type: String,
      required: true
    },
    autosize: {
      type: Object,
      default: null
    },
    errorMessageFormatter: {
      type: Function,
      default: value => value
    },
    ancestorIds: {
      type: Array,
      default: () => []
    },
    renderChildren: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    isChild () {
      return this.questionNumber === null
    },
    nodeDepth () {
      return this.ancestorIds.length
    },
    usesInlineChildren () {
      if (this.displayMode === 'candidate-answer') return this.nodeDepth < 2
      if (this.displayMode === 'hr-sidebar') return this.nodeDepth < 1
      return true
    },
    nextAncestorIds () {
      return [...this.ancestorIds, String(this.question.id)]
    },
    selectedOptionIds () {
      const answer = Array.isArray(this.question.answer) ? this.question.answer : [this.question.answer]
      return answer.filter(value => value !== null && value !== undefined && value !== '').map(String)
    },
    selectedChildren () {
      if (![QUESTION_TYPE.SELECT, QUESTION_TYPE.MULTIPLE, QUESTION_TYPE.RADIO, QUESTION_TYPE.CHECKBOX].includes(this.question.type)) return []
      return (this.question.optionList || []).reduce((children, option) => {
        if (!this.selectedOptionIds.includes(String(option.value))) return children
        return children.concat(this.childrenForOption(option.value))
      }, [])
    }
  },
  mounted () {
    this.$emit('register-question', this.question.id, this.$refs.questionControl)
  },
  beforeDestroy () {
    this.$emit('unregister-question', this.question.id, this.$refs.questionControl)
  },
  methods: {
    childrenForOption (optionId) {
      return this.questions.reduce((children, child) => {
        const childId = String(child.id)
        const placement = this.placementMap[childId]
        if (placement && placement.parentQuestionId === String(this.question.id) &&
          placement.parentOptionId === String(optionId) &&
          !this.nextAncestorIds.includes(childId)) {
          children.push(child)
        }
        return children
      }, [])
    }
  }
}
</script>

<style lang="scss" scoped>
.question-answer-node {
  box-sizing: border-box;
  min-width: 0;
}

.question-answer-node.candidate-answer.is-child:not(.is-deep) {
  width: 100%;
  margin-top: 5px;
  padding: 10px;
  border: 0;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px #dcdfe6;
}

.question-answer-node.candidate-answer.is-deep {
  display: contents;
}

.question-answer-node.candidate-answer.is-deep > .question-card {
  box-sizing: border-box;
  width: 100%;
  margin: 5px 0 0;
  padding: 10px;
  border: 0;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px #dcdfe6;
}

.question-answer-node.candidate-answer.is-later-deep > .question-card {
  padding: 0 10px 10px;

  ::v-deep .question-title-group .question-title {
    line-height: 32px;
  }
}

.question-answer-node.hr-sidebar:not(.is-child) {
  margin-bottom: 16px;
  padding: 10px 0;
  background: #f7f8fa;
}

.question-answer-node.hr-sidebar > .question-card {
  margin-bottom: 0;
}

.question-answer-node.hr-sidebar.is-child {
  display: contents;
}

.question-answer-node.hr-sidebar.is-child > .question-card {
  box-sizing: border-box;
  width: 100%;
  margin: 8px 0 0;
  padding: 10px;
  border-radius: 4px;
  background: #fff;
}
</style>
